<template>
  <main-layout
    class="pb-4 pt-0"
    small
  >
    <div class="d-flex justify-content-between align-items-center">
      <base-breadcrumb :routes="routes" />
    </div>
    <base-card>
      <base-heading class="d-inline">
        Time of Appointment
      </base-heading>
      <base-icon
        icon="schedule"
        class="mb-2 ml-3"
      />
      <div class="d-flex justify-content-start">
        <div>
          <div
            tabindex="0"
            class="d-flex mr-2 h-100 date_selector align-items-center"
            style="width: 184px"
            @click.stop="toggleStartCalendarMethod"
          >
            <base-text
              light
              class="ml-2"
              style="font-size: 14px;"
            >
              {{ selectedStartDate || '--' | nicedate('small') }}
            </base-text>
            <base-icon
              v-if="toggleStartCalendar"
              class="ml-auto mr-2"
              icon="orange-arrow-up"
            />
            <base-icon
              v-else
              class="ml-auto mr-2"
              icon="orange-arrow-down"
            />
          </div>
          <v-date-picker
            v-if="toggleStartCalendar"
            v-model="selectedStartDate"
            v-click-outside="toggleStartCalendarMethod"
            class="position-absolute"
            :min-date="new Date()"
          />
        </div>
        <div>
          <base-selector
            ref="startTimeSelector"
            v-click-outside="closeStartTimeSelector"
            width="150"
            height="200"
            :items="startScheduleOptions"
            :value="selectedStartTime"
            @input="onStartChange"
          />
        </div>
        <span class="mx-3">to</span>
        <div>
          <div
            tabindex="2"
            class="d-flex mr-2 h-100 date_selector align-items-center"
            style="width: 184px"
            @click.stop="toggleEndCalendarMethod"
          >
            <base-text
              light
              class="ml-2"
              style="font-size: 14px;"
            >
              {{ selectedEndDate || '--' | nicedate('small') }}
            </base-text>
            <base-icon
              v-if="toggleEndCalendar"
              class="ml-auto mr-2"
              icon="orange-arrow-up"
            />
            <base-icon
              v-else
              class="ml-auto mr-2"
              icon="orange-arrow-down"
            />
          </div>
          <v-date-picker
            v-if="toggleEndCalendar"
            v-model="selectedEndDate"
            v-click-outside="toggleEndCalendarMethod"
            class="position-absolute"
            :min-date="selectedStartDate !== '' ? selectedStartDate : new Date()"
          />
        </div>
        <div style="z-index: 2;">
          <base-selector
            ref="endTimeSelector"
            v-click-outside="closeEndTimeSelector"
            width="150"
            height="200"
            :items="endScheduleOptions"
            :value="selectedEndTime"
            @input="onEndChange"
          />
        </div>
      </div>
      <base-heading class="mt-3">
        Appointment Name
      </base-heading>
      <input
        v-model="appointmentName"
        type="text"
        class="w-100 pl-2"
      >
      <div class="mt-3">
        <base-heading class="text-nowrap">
          Type of Appointment
        </base-heading>
        <div class="d-flex align-items-center">
          <base-selector
            ref="apptType"
            v-click-outside="closeApptType"
            width="184"
            class="mr-2"
            :value="selectedType"
            :items="appointmentType"
            @input="onTypeChange"
          />
        </div>
      </div>
    </base-card>
    <base-card
      v-if="selectedType.value.length"
      class="mt-3"
    >
      <intro-editor
        v-model="intro"
        :hide-intro="!willSendInvite"
        :editing="isEditing"
        :invited-recipients="invitedRecipients"
        :hide-groups="recommendation"
        class="w-100 mt-3"
        @update-recipients="onRecipientsUpdate"
      >
        <template
          #heading
        >
          <div class="mt-n3" />
        </template>
      </intro-editor>
    </base-card>
    <div class="d-flex mt-4 justify-content-end align-items-center">
      <base-text
        class="cancel_button"
        @click="clearSection()"
      >
        Cancel
      </base-text>
      <base-button
        v-if="selectedType.value.length"
        class="ml-4"
        inverse
        :disabled="!validateAppointment()"
        @click="completeAppointment"
      >
        <template v-if="loading">
          <base-loading />
        </template>
        <template v-else>
          <base-text>
            {{ isEditing || !willSendInvite ? 'Save' : 'Send invite' }}
          </base-text>
        </template>
      </base-button>
    </div>
  </main-layout>
</template>
<script>

import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import IntroEditor from '@/components/Widgets/Appointments/IntroEditor';
import { EventBus } from '@/event-bus';
import emailEngine from '@/components/mixins/emailEngine';
import { APPOINTMENT_TYPES, MIXPANEL_EVENTS } from '@/_utilities/Enums';
import { sendToMixPanel } from '@/_utilities/seerMixPanel';

import _ from 'lodash';

export default {
  name: 'AppointmentShow',
  components: {
    IntroEditor,
    VDatePicker,
  },
  mixins: [emailEngine],
  data() {
    return {
      APPOINTMENT_TYPES,
      intro: '',
      appointmentName: '',
      appointmentType: [
        APPOINTMENT_TYPES.VIRTUAL,
        APPOINTMENT_TYPES.RECOMMENDATION,
      ].map((type) => ({
        name: this.typeToLabel(type),
        value: type,
        disabled: false,
      })),
      selectedType: { name: '', value: '' },
      selectedStartTime: { name: '' },
      selectedEndTime: { name: '' },
      selectedStartDate: '',
      selectedEndDate: '',
      toggleStartCalendar: false,
      toggleEndCalendar: false,
      recipients: [],
      invitedRecipients: [],
      preFilledIntro: '',
      notifyUsers: false,
    };
  },
  computed: {
    routes() {
      return [{
        name: 'Appointments',
        link: '/appointments',
      }, {
        name: 'New appointment',
      }];
    },
    loading() {
      return this.$store.state.appointments.isLoading;
    },
    /* customers() {
      return this.recipients.filter(item => !item.group).map(item => item.object.pk);
    },
    groups() {
      return this.recipients.filter(item => item.group).map(item => item.object.pk);
    }, */
    startScheduleOptions() {
      // Creates the start time schedule options, you cant book appointments in the past, unless they're around the last 15 min
      const hourStrings = [];
      const indexes = [...Array(24).keys()];
      indexes.forEach((index) => {
        const hour = index;
        [...Array(4).keys()].forEach((minutes) => {
          const time = this.$moment({ hour, minute: 15 * minutes }).format('hh:mmA');
          const hideHour = this.startHoursVerifier(hour, minutes);
          hourStrings.push({ name: time, disabled: hideHour });
        });
      });
      return hourStrings;
    },
    endScheduleOptions() {
      // Creates the end time schedule options, you cant book appointments that are past to start time/date
      const hourStrings = [];
      const indexes = [...Array(24).keys()];
      indexes.forEach((index) => {
        const hour = index;
        [...Array(4).keys()].forEach((minutes) => {
          const time = this.$moment({ hour, minute: 15 * minutes }).format('hh:mmA');
          hourStrings.push({ name: time, disabled: this.endHoursVerifier(hour, minutes) });
        });
      });
      return hourStrings;
    },
    isEditing() {
      return Object.keys(this.$route.params).length > 0;
    },
    appointmentData() {
      return this.$store.state.appointments.item;
    },
    willSendInvite() {
      return this.selectedType?.value?.length > 0 && !this.recommendation;
    },
    recommendation() {
      return this.selectedType?.value === APPOINTMENT_TYPES.RECOMMENDATION;
    },
  },
  watch: {
    selectedStartDate(newval) {
      if (this.selectedEndDate === '' || newval > this.selectedEndDate) {
        this.selectedEndDate = newval;
      }
      if (this.selectedStartTime.name !== '' && this.timeDisabled(this.selectedStartTime, this.startScheduleOptions)) {
        this.selectedStartTime = this.selectFirstAvailableTime(this.startScheduleOptions);
      }
      this.toggleStartCalendar = false;
    },
    selectedEndDate(newval) {
      if (this.selectedStartDate === '') {
        this.selectedStartDate = newval;
      } else if (this.selectedStartDate.toDateString() === newval.toDateString() && this.selectedStartTime.name !== '' && this.timeDisabled(this.selectedEndTime, this.endScheduleOptions)) {
        this.selectedEndTime = this.selectFirstAvailableTime(this.endScheduleOptions);
      }
      this.toggleEndCalendar = false;
    },
    selectedType() {
      this.onRecipientsUpdate(this.recipients);
    },
  },
  mounted() {
    if (this.isEditing) {
      this.$store.dispatch('appointments/getItem', this.$route.params.id).then(() => {
        this.populateData();
        console.log('mounted startTime:', this.selectedStartTime);
      });
    }
  },
  methods: {
    timeDisabled(selectedTime, options) {
      const selectedTimeOption = options.find((item) => item.name === selectedTime.name);
      return selectedTimeOption.disabled;
    },
    closeStartTimeSelector() {
      this.$nextTick().then(() => {
        const el = this.$refs.startTimeSelector;
        if (el.selector) el.closeSelector();
      });
    },
    closeEndTimeSelector() {
      this.$nextTick().then(() => {
        const el = this.$refs.endTimeSelector;
        if (el.selector) el.closeSelector();
      });
    },
    closeApptType() {
      this.$nextTick().then(() => {
        this.$refs.apptType.closeSelector();
      });
    },
    toggleEndCalendarMethod() {
      this.toggleStartCalendar = false; // just in case
      this.toggleEndCalendar = !this.toggleEndCalendar;

      if (this.toggleEndCalendar) {
        this.$refs.startTimeSelector.closeSelector();
        this.$refs.endTimeSelector.closeSelector();
      }
    },
    toggleStartCalendarMethod(evt) {
      this.toggleEndCalendar = false; // just in case
      this.toggleStartCalendar = !this.toggleStartCalendar;

      if (this.toggleStartCalendar) {
        // evt.preventDefault()
        this.$refs.startTimeSelector.closeSelector();
        this.$refs.endTimeSelector.closeSelector();
      }
    },
    selectFirstAvailableTime(scheduleOptions) {
      return scheduleOptions.find((item) => !item.disabled);
    },
    endHoursVerifier(hour, minutes) {
      if (this.selectedStartTime.name === '') {
        return true;
      }
      const startMoment = this.$moment(this.selectedStartTime.name.substring(0, 7), 'hh:mmA');
      startMoment.minutes(startMoment.minutes() + 15);
      return this.selectedStartTime.name !== ''
              && this.$moment({ hour, minute: 15 * minutes }).isBefore(startMoment);
    },
    startHoursVerifier(hour, minutes) {
      // Function that checks if times are selectable or not depending on selected dates and current minutes
      let hours = hour;
      const furtherMinutes = (15 * (minutes + 1)) < 60 ? (15 * (minutes + 1)) : 0;
      if (furtherMinutes === 0) hours += 1;
      if (this.selectedStartDate === '') {
        return this.$moment({ hours, minute: furtherMinutes }).isBefore(this.$moment());
      }
      const startDate = new Date(this.selectedStartDate);
      startDate.setHours(hours);
      startDate.setMinutes(furtherMinutes);
      return this.$moment(startDate).isBefore(this.$moment());
    },
    checkDates(payload) {
      if (new Date(this.appointmentData.start_date).toISOString() !== payload.start_date.toISOString()
        || new Date(this.appointmentData.end_date).toISOString() !== payload.end_date.toISOString()) {
        this.notifyUsers = true;
      }
    },
    clearSection() {
      this.$router.push('/appointments');
    },
    onTypeChange(newval) {
      this.selectedType = newval;
    },
    onStartChange(newval) {
      this.selectedStartTime = newval;
      const start = this.startScheduleOptions.map((item) => item.name).indexOf(newval.name);
      if (this.selectedEndTime.name === '') {
        if (start + 4 >= this.startScheduleOptions.length) {
          this.selectedEndTime = this.startScheduleOptions.last();
        } else {
          this.selectedEndTime = this.startScheduleOptions[start + 4];
        }
      } else {
        this.selectedEndTime = this.startScheduleOptions[start + 4];
      }
    },
    onEndChange(newval) {
      this.selectedEndTime = newval;
    },
    validateAppointment() {
      const hasRecipients = this.recipients.length > 0;
      const hasUniqueNonGroupRecipient = this.recipients.filter((r) => !r.group).length === 1;
      if (this.selectedType.name === ''
        || this.selectedStartDate === ''
        || this.selectedStartTime.name === ''
        || this.selectedEndDate === ''
        || this.selectedEndTime.name === ''
        || (!this.recommendation && this.appointmentName === '')
        || (!this.recommendation && this.intro === '')
        || (this.recommendation && !hasUniqueNonGroupRecipient)
        || !hasRecipients) {
        return false;
      }
      return true;
    },
    completeAppointment() {
      if (!this.validateAppointment()) {
        EventBus.$emit('toast-error', 'You must fill all empty fields.');
        return;
      }
      if (this.loading) return;
      // Send invites and create appointment

      const payload = {
        owner: this.$store.state.account.user.profile.pk,
        created_at_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        subject: this.appointmentName?.length ? this.appointmentName
          : (this.recommendation ? 'Recommendation' : ''),
        intro: this.intro,
        start_date: this.formatDate(this.selectedStartDate, this.selectedStartTime.name),
        end_date: this.formatDate(this.selectedEndDate, this.selectedEndTime.name),
        appointment_type: this.selectedType.value,
      };
      if (this.emailEngine.name === 'Email' || !this.willSendInvite) {
        // If not sending invites, bypass email engine login
        if (this.isEditing) {
          this.patchAppointment(payload);
        } else {
          this.createNewAppointment(payload);
        }
        return;
      }
      if (this.isEditing) {
        this.emailEngine.action(payload, this.patchAppointment);
        return;
      }
      this.emailEngine.action(payload, this.createNewAppointment);
    },
    async createNewAppointment(payload) {
      try {
        const response = await this.$store.dispatch('appointments/newItem', payload);
        const inviteesPayload = [];
        const groupPayload = [];
        this.recipients.forEach((item) => {
          if (item.group) {
            groupPayload.push({ group: item.object.pk, appointment: response.data.pk });
          } else {
            inviteesPayload.push({ customer: item.object.pk, appointment: response.data.pk });
          }
        });
        await this.$store.dispatch('appointments/createInvites',
          {
            appointment: response.data.pk, groups: groupPayload, invitees: inviteesPayload, authCode: this.authCode,
          });
        this.authCode = null;
        const invertedAppointmentTypesEnum = _.invert(APPOINTMENT_TYPES);
        sendToMixPanel(MIXPANEL_EVENTS.newAppointment, {
          appointment_type: invertedAppointmentTypesEnum[this.selectedType?.value],
        });
        this.$router.push(`/appointments/${response.data.pk}/view`);
      } catch (e) {
        EventBus.$emit('toast-error', 'There\'s been an error while creating this appointment, please try again later.');
      }
    },
    async patchAppointment(payload) {
      const { id } = this.$route.params;
      this.checkDates(payload);
      try {
        await this.$store.dispatch('appointments/updateAppointment', { id, payload });
        const inviteesPayload = [];
        const groupPayload = [];
        this.recipients.forEach((item) => {
          if (item.group) {
            groupPayload.push(item.object.pk);
          } else {
            inviteesPayload.push(item.object.pk);
          }
        });

        const notify = this.notifyUsers;
        const { authCode } = this;
        await this.$store.dispatch('appointments/updateInvites', {
          id, inviteesPayload, groupPayload, notify, authCode,
        });
        this.$router.push(`/appointments/${this.$route.params.id}/view`);
      } catch (e) {
        EventBus.$emit('toast-error', 'There\'s been an error while updating this appointment info, please try again later.');
      }
    },
    onRecipientsUpdate(newval) {
      // Filter out groups if appointment type is recommendation
      this.recipients = newval.filter((item) => !item.group || (!this.recommendation));
    },
    formatDate(date, time) {
      let hour = time.substring(0, 2).replace(':', '');
      const minutes = time.substring(3, 5);
      const initials = time.substring(5, 7);
      if (initials === 'PM') {
        if (hour !== '12') {
          hour = parseInt(hour, 10) + 12;
        }
      }
      const newDate = new Date(date.toISOString());
      newDate.setHours(hour, minutes);
      return newDate;
    },
    populateData() {
      const start = new Date(this.appointmentData.start_date);
      const end = new Date(this.appointmentData.end_date);
      this.appointmentName = this.appointmentData.subject;
      this.selectedType = {
        name: this.typeToLabel(this.appointmentData.appointment_type),
        value: this.appointmentData.appointment_type,
      };
      this.selectedStartDate = start;
      this.selectedEndDate = end;
      this.selectedStartTime = { name: `${this.$moment(start).format('hh:mmA')}` };
      this.selectedEndTime = { name: `${this.$moment(end).format('hh:mmA')}` };
      this.invitedRecipients = this.appointmentData.invitations.filter((item) => item.customer != null);
      this.intro = this.appointmentData.intro !== null ? this.appointmentData.intro : ' ';
    },
    typeToLabel(typeKey) {
      return this.$options.filters.capitalize(
        Object.entries(APPOINTMENT_TYPES)
          .find(([key, value]) => value === typeKey)[0]
          .replace('_', ' ').toLowerCase(),
      );
    },
  },
};
</script>
<style lang="scss"
  scoped>

.date_selector {
  border: 1px solid #D4D4D4;
  border-radius: 3px;
  min-height: 28px;
  cursor: pointer;
}

.products-widget {
  ::v-deep .card-body {
    background-color: $gray-light;
    border: 1px solid #D4D4D4;
    border-radius: 5px;
  }
}

input {
  border: 1px solid #D4D4D4;
  border-radius: 5px;
  min-height: 34px;
}

.cancel_button {
  color: #E9AD7D;
  cursor: pointer;
}

::v-deep .ti-autocomplete {
  max-height: 300px;
  overflow-y: auto;
}

::v-deep .base-text {
  font-size: 14px;
}
::v-deep .vc-container {
  z-index: 100000;
}

::v-deep .vc-highlight {
  background-color: $clementine !important;
}

</style>
